import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface StarfieldProps {
  speed?: number;
  lightColor?: string; // Hex
  darkColor?: string; // Hex
}

export function StarfieldBackground({ speed = 1, lightColor = '#ffffff', darkColor = '#000000' }: StarfieldProps) {
  const count = 3000; // Reduced count for better performance with textures
  const mesh = useRef<THREE.Points>(null);
  
  // Generate Star Texture
  const starTexture = useMemo(() => {
    // ... (texture generation remains same, but maybe we can invert it? No, texture is alpha map mostly)
    // Actually texture is white with alpha. Color is set by vertex colors.
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = 'rgba(0,0,0,0)';
      ctx.fillRect(0, 0, 32, 32);
      
      // Draw 4-pointed star
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      const cx = 16;
      const cy = 16;
      const spikes = 4;
      const outerRadius = 15;
      const innerRadius = 4;
      
      for (let i = 0; i < spikes * 2; i++) {
        const r = i % 2 === 0 ? outerRadius : innerRadius;
        const angle = (i / (spikes * 2)) * Math.PI * 2;
        ctx.lineTo(cx + Math.cos(angle) * r, cy + Math.sin(angle) * r);
      }
      ctx.closePath();
      ctx.fill();
      
      // Add a glow
      const gradient = ctx.createRadialGradient(cx, cy, innerRadius, cx, cy, outerRadius);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = gradient;
      ctx.fill();
    }
    const texture = new THREE.CanvasTexture(canvas);
    return texture;
  }, []);

  const [positions, sizes, velocities] = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    const velocities = new Float32Array(count * 3); // Individual velocities
    
    // Simple seeded random to satisfy purity requirements
    let seed = 1234;
    const random = () => {
        const x = Math.sin(seed++) * 10000;
        return x - Math.floor(x);
    };

    for (let i = 0; i < count; i++) {
      // Random positions in a large cube
      const x = (random() - 0.5) * 200;
      const y = (random() - 0.5) * 200;
      const z = (random() - 0.5) * 200;
      
      positions[i * 3] = x;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = z;

      // Random sizes - varied for "bindi dots" vs "stars"
      sizes[i] = random() < 0.8 ? random() * 0.5 + 0.1 : random() * 1.5 + 0.5;

      // Random velocities for "bumping into each other" feel (Brownian-ish)
      velocities[i * 3] = (random() - 0.5) * 0.02;
      velocities[i * 3 + 1] = (random() - 0.5) * 0.02;
      velocities[i * 3 + 2] = (random() - 0.5) * 0.02;
    }
    return [positions, sizes, velocities];
  }, []);

  // Update colors based on lightColor
  const colors = useMemo(() => {
    const colors = new Float32Array(count * 3);
    const baseColor = new THREE.Color(lightColor);
    
    // Use a simple seeded random for deterministic color variations
    let seed = 123;
    const seededRandom = () => {
      seed = (seed * 16807) % 2147483647;
      return (seed - 1) / 2147483646;
    };

    for (let i = 0; i < count; i++) {
        const color = baseColor.clone();
        // Add slight variation
        color.offsetHSL(0, 0, (seededRandom() - 0.5) * 0.2);
        
        colors[i * 3] = color.r;
        colors[i * 3 + 1] = color.g;
        colors[i * 3 + 2] = color.b;
    }
    return colors;
  }, [lightColor]);

  // Background Color (Dark Tones)
  const bgColor = useMemo(() => {
      return new THREE.Color(darkColor);
  }, [darkColor]);

  useFrame((state) => {
    if (!mesh.current) return;
    
    const time = state.clock.getElapsedTime();
    const positions = mesh.current.geometry.attributes.position.array as Float32Array;
    
    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      
      // Base Fly movement (Z-axis)
      positions[i3 + 2] += speed * 0.2;
      
      // Add individual random movement
      positions[i3] += velocities[i3] * speed * 5;
      positions[i3 + 1] += velocities[i3 + 1] * speed * 5;
      positions[i3 + 2] += velocities[i3 + 2] * speed * 5;

      // Reset if out of bounds
      if (positions[i3 + 2] > 100) {
        positions[i3 + 2] = -100;
        // Use a deterministic-ish reset based on index to avoid Math.random() in useFrame
        positions[i3] = ((i * 123.456) % 200) - 100;
        positions[i3 + 1] = ((i * 789.012) % 200) - 100;
      }
      
      // Spiral/Vortex effect
      const x = positions[i3];
      const y = positions[i3 + 1];
      const dist = Math.sqrt(x*x + y*y);
      const angle = 0.0001 * speed * dist * Math.sin(time * 0.2);
      
      positions[i3] = x * Math.cos(angle) - y * Math.sin(angle);
      positions[i3 + 1] = x * Math.sin(angle) + y * Math.cos(angle);
    }
    
    mesh.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <>
        {bgColor && <color attach="background" args={[bgColor.getStyle()]} />}
        <points ref={mesh}>
        <bufferGeometry>
            <bufferAttribute
            attach="attributes-position"
            count={count}
            array={positions}
            itemSize={3}
            />
            <bufferAttribute
            attach="attributes-color"
            count={count}
            array={colors}
            itemSize={3}
            />
            <bufferAttribute
            attach="attributes-size"
            count={count}
            array={sizes}
            itemSize={1}
            />
        </bufferGeometry>
        <pointsMaterial
            map={starTexture}
            size={1}
            vertexColors
            transparent
            opacity={0.9}
            sizeAttenuation
            depthWrite={false}
            blending={THREE.AdditiveBlending}
            alphaTest={0.01}
        />
        </points>
    </>
  );
}
